import { useState } from "react";
import {
  useCreate,
  useGetIdentity,
  useNotify,
  useRefresh,
  useTranslate,
} from "ra-core";
import { FolderPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { findOriginLabel } from "@/lib/originChannels";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { CompanyFolder } from "../types";

function slugPart(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

export function CreateCompanyFolderButton() {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const { companySectors } = useConfigurationContext();
  const [create] = useCreate<CompanyFolder>();
  const [isOpen, setIsOpen] = useState(false);
  const [sector, setSector] = useState(companySectors[0]?.value ?? "other");
  const [city, setCity] = useState("Bogotá");
  const [name, setName] = useState("");
  const [isPending, setIsPending] = useState(false);

  const sectorLabel =
    companySectors.find((item) => item.value === sector)?.label ?? sector;
  const suggestedName = [slugPart(sectorLabel), slugPart(city)]
    .filter(Boolean)
    .join("_");

  const handleOpen = (): void => {
    setName(suggestedName);
    setIsOpen(true);
  };

  const handleCreate = async (): Promise<void> => {
    const folderName = (name.trim() || suggestedName).trim();
    if (!folderName) {
      return;
    }
    setIsPending(true);
    try {
      await create(
        "company_lists",
        {
          data: {
            name: folderName,
            sector,
            city: city.trim(),
            origin: "prospector_maps",
            sales_id: identity?.id,
          },
        },
        { returnPromise: true },
      );
      notify("resources.companies.folders.created", {
        type: "info",
        messageArgs: { _: "Carpeta creada" },
      });
      setIsOpen(false);
      refresh();
    } catch {
      notify("resources.companies.folders.create_error", {
        type: "error",
        messageArgs: { _: "No se pudo crear la carpeta" },
      });
    } finally {
      setIsPending(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="flex items-center gap-2"
        onClick={handleOpen}
      >
        <FolderPlus className="h-4 w-4" aria-hidden="true" />
        {translate("resources.companies.folders.create", {
          _: "Crear carpeta",
        })}
      </Button>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {translate("resources.companies.folders.create_title", {
                _: "Nueva carpeta de empresas",
              })}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {translate("resources.companies.folders.create_help", {
              _: "El sector queda en la carpeta. La zona va en el título, por ejemplo salones_belleza_Bogotá.",
            })}
          </p>
          <div className="flex flex-col gap-2">
            <Label htmlFor="folder-sector">
              {translate("resources.companies.fields.sector", { _: "Sector" })}
            </Label>
            <select
              id="folder-sector"
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={sector}
              onChange={(event) => {
                setSector(event.target.value);
                const nextLabel =
                  companySectors.find(
                    (item) => item.value === event.target.value,
                  )?.label ?? event.target.value;
                setName(
                  [slugPart(nextLabel), slugPart(city)]
                    .filter(Boolean)
                    .join("_"),
                );
              }}
            >
              {companySectors.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="folder-city">
              {translate("resources.companies.fields.city", {
                _: "Zona / ciudad",
              })}
            </Label>
            <Input
              id="folder-city"
              value={city}
              onChange={(event) => {
                setCity(event.target.value);
                setName(
                  [slugPart(sectorLabel), slugPart(event.target.value)]
                    .filter(Boolean)
                    .join("_"),
                );
              }}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="folder-name">
              {translate("resources.companies.folders.name", {
                _: "Nombre de la carpeta",
              })}
            </Label>
            <Input
              id="folder-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {findOriginLabel("prospector_maps") || "CSV / Prospector Maps"}
          </p>
          <Button
            type="button"
            disabled={isPending}
            onClick={() => {
              void handleCreate();
            }}
          >
            {translate("resources.companies.folders.create", {
              _: "Crear carpeta",
            })}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
